// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { TippyPopupStyle } from "@guestsupply/App/Components/Tippy/Style";
import TippyPopup from "@guestsupply/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import CaretDown from "@guestsupply/XDL/Icons/CaretDown";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

// links
const enum linkFields {
    title = "title",
    linkList = "linkList",
    destination = "destination",
    openInNewWindow = "openInNewWindow",
}
// subLinks
const enum subLinkFields {
    title = "title",
    destination = "destination",
    openInNewWindow = "openInNewWindow",
}

export interface subLinkModel {
    fields: {
        [subLinkFields.title]: string;
        [subLinkFields.destination]: LinkFieldValue;
        [subLinkFields.openInNewWindow]: boolean;
    };
}
export interface LinkModel {
    fields: {
        [linkFields.title]: string;
        [linkFields.linkList]: subLinkModel[];
        [linkFields.destination]: LinkFieldValue;
        [linkFields.openInNewWindow]: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}
interface HeaderLinkProps {
    destination: LinkFieldValue;
    title: string;
    openInNewWindow: boolean;
    isTippyLink?: boolean;
    HeaderTitleLink?: boolean;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-CANGES
    linkList?: LinkPresentationProps;
    menuItemIcon?: IconProps;
    tippyPopup?: TippyPopupStyle;
    linkListWrapper?: InjectableCss;
    parentLink?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            text-align: right;
            padding: 0 0 23px 0;
            margin-right: -9px;
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
            css: css`
                margin: 0 9px 0 9px;
                display: inline-block;
                &&& {
                    color: ${getColor("text.main")};
                }
            `,
        },
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

// xng-change: add google event
const mapStateToProps = (state: CustomAppState) => ({
    xGoogleAnalytics: state.xGoogleAnalytics,
    currentBillTo: getCurrentBillToState(state).value,
    currentPage: getCurrentPage(state).type,
});

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps>;

export const HeaderLinkList: React.FC<Props> = ({
    fields,
    extendedStyles,
    history,
    // xng-change: add google event
    xGoogleAnalytics,
    currentPage,
    currentBillTo,
}) => {
    const links = fields.links;
    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (links?.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };
    const downIcon = <StyledIcon src={CaretDown} {...styles.menuItemIcon} />;

    // xng-change: add google event
    const getDataLayerEvent = (title: string, url?: string | undefined) => {
        const dataLayer = getDataLayer();

        let googleAnalyticsData = {
            page_path: url || currentPage,
            brand_code: currentBillTo?.properties?.brand,
            exclusive: currentBillTo?.properties?.exclusive,
            group_descr: currentBillTo?.properties?.groupDescription,
        };

        if (xGoogleAnalytics.includePaymentPortalEvent && title === "Payment Portal") {
            googleAnalyticsData = {
                ...googleAnalyticsData,
                event: GOOGLE_EVENTS.PAYMENT_PORTAL,
                affiliation: currentBillTo?.companyName,
            } as any;
        } else if (xGoogleAnalytics.includeNavigationBarEvent) {
            googleAnalyticsData = {
                ...googleAnalyticsData,
                event: GOOGLE_EVENTS.NAVIGATION_BAR,
                click_text: title,
            } as any;
        }
        dataLayer.push(googleAnalyticsData);
    };

    const HeaderLink = ({
        destination,
        title,
        openInNewWindow,
        isTippyLink = false,
        HeaderTitleLink = false,
    }: HeaderLinkProps) => {
        const { url } = useGetLink(destination);
        const customStyles = HeaderTitleLink ? styles.parentLink : isTippyLink ? styles.linkList : styles.link;

        return (
            <Link
                className="list-link"
                key={`${destination}.${title}`}
                href={url}
                target={openInNewWindow ? "_blank" : ""}
                onClick={() => {
                    hideAll();
                    // xng-change: add google event
                    getDataLayerEvent(title, url);
                }}
                aria-label={title}
                {...customStyles}
            >
                {title}
            </Link>
        );
    };
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                {links?.map((link, index) => {
                    if (link.fields.linkList?.length > 0) {
                        return (
                            <TippyPopup
                                key={index}
                                extendedStyles={styles.tippyPopup}
                                content={
                                    <StyledWrapper {...styles.linkListWrapper}>
                                        <HeaderLink
                                            destination={link.fields.destination}
                                            title={link.fields.title}
                                            openInNewWindow={link.fields.openInNewWindow}
                                            aria-label={link.fields.title}
                                            HeaderTitleLink={true}
                                        />
                                        {link.fields.linkList.map((link, index) => {
                                            return (
                                                <HeaderLink
                                                    key={link.fields.title}
                                                    title={link.fields.title}
                                                    destination={link.fields.destination}
                                                    openInNewWindow={link.fields.openInNewWindow}
                                                    isTippyLink={true}
                                                />
                                            );
                                        })}
                                    </StyledWrapper>
                                }
                                trigger="click"
                                arrow={false}
                                offset={[0, 0]}
                                maxWidth={300}
                                placement="bottom-end"
                                type="menu"
                                showCloseButton={false}
                                interactive={true}
                                animation={false}
                                appendTo="parent"
                                onTrigger={instance => {
                                    instance.reference.classList.add("active");
                                }}
                                applyGuuter={false}
                                popperOptions={{
                                    strategy: "absolute",
                                    modifiers: [
                                        {
                                            name: "preventOverflow",
                                            options: {
                                                padding: 0,
                                            },
                                        },
                                        {
                                            name: "computeStyles",
                                            options: {
                                                adaptive: true,
                                                roundOffsets: ({ x, y }: any) => ({
                                                    x,
                                                    y,
                                                }),
                                            },
                                        },
                                    ],
                                }}
                                onHidden={instance => {
                                    instance.reference.classList.remove("active");
                                }}
                            >
                                {/* XNG-Change: use HeaderClickable component */}
                                <Link
                                    className="list-link"
                                    key={link.fields.title}
                                    aria-label={link.fields.title}
                                    {...styles.link}
                                    // xng-change: add google event
                                    onClick={() => getDataLayerEvent(link.fields.title)}
                                >
                                    <Typography>{link.fields.title}</Typography>
                                    {downIcon}
                                </Link>
                            </TippyPopup>
                        );
                    }

                    return (
                        <HeaderLink
                            key={`${link.fields.destination}.${link.fields.title}`}
                            title={link.fields.title}
                            destination={link.fields.destination}
                            openInNewWindow={link.fields.openInNewWindow}
                        />
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(HeaderLinkList)),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: linkFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: linkFields.destination,
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linkFields.linkList,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            if (item.fields.title) {
                                return item.fields.title;
                            }
                        },
                        fieldDefinitions: [
                            {
                                name: subLinkFields.title,
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isRequired: true,
                            },
                            {
                                name: subLinkFields.destination,
                                editorTemplate: "LinkField",
                                defaultValue: { type: "Page", value: "" },
                                isRequired: true,
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
